import FilterListIcon from "@mui/icons-material/FilterList";
import { useMediaQuery } from "@mui/material";
import Chip from "@mui/material/Chip";
import { styled, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { PageTab } from "app/routes/proposals/tabs";
import NumberBadge from "common/components/number-badge";
import {
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { useNavigate, useSearchParams } from "react-router";
import {
  ProposalBoxType
} from "work/entities/proposal/api/request-contracts/proposal-parameters";
import Proposal from "work/entities/proposal/proposal";

const ProposalToolbarButtonsContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
    },
    display: "flex",
    alignItems: "end",
    flexDirection: "column",
    gap: theme.spacing(1),
  }));
const ProposalToolbarButtons = styled("div")(
  () => ({
    display: "flex",
    alignItems: "end",
    flexWrap: "nowrap",
  }));
const ProposalTableTopToolbarContainer = styled("div")(
  () => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }));
const LowerTopToolbarContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "left",
    width: "100%",
  }));
const TableTitle = styled(Typography)(
  ({ theme }) => ({
    paddingLeft: theme.spacing(1),
  }));
const FilterButtons = styled("div")(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    "& > .MuiChip-root": {
      [theme.breakpoints.down("xl")]: {
        marginBottom: theme.spacing(0.5),
      },
      marginLeft: theme.spacing(0.5),
    },
  }));

type ProposalToolbarProps = {
  table: TableInstance<Proposal>,
  activeTab: PageTab,
  activeProposalCount?: number,
  draftProposalCount?: number,
  archivedProposalCount?: number,
};

export default function ProposalToolbar(props: Readonly<ProposalToolbarProps>) {
  const {
    table,
    activeTab,
    activeProposalCount,
    draftProposalCount,
    archivedProposalCount
  } = props;

  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const currentBoxType = searchParams.get("boxType");

  function handleChangeBoxType(newBoxType: ProposalBoxType) {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (currentBoxType !== newBoxType) {
        params.set("boxType", newBoxType?.valueOf());
      } else params.delete("boxType");
      return params;
    });
  }

  return (
    <ProposalTableTopToolbarContainer>
      <TableTitle variant="h4">Proposals</TableTitle>
      <LowerTopToolbarContainer>
        <Tabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_event, newValue) =>
            navigate(`/proposals/${newValue}`)
          }
        >
          <Tab
            value={PageTab.Active}
            label={
              <NumberBadge
                tooltip={`${activeProposalCount ?? 0} active proposal(s)`}
                badgeCount={activeProposalCount ?? 0}
                color={"info"}
                text={PageTab.Active}
              />
            }
          />
          <Tab
            value={PageTab.Drafts}
            label={
              <NumberBadge
                tooltip={`${draftProposalCount ?? 0} draft proposal(s)`}
                badgeCount={draftProposalCount ?? 0}
                color={"info"}
                text={PageTab.Drafts}
              />
            }
          />
          <Tab
            value={PageTab.Archived}
            label={
              <NumberBadge
                tooltip={`${archivedProposalCount ?? 0} archived proposal(s)`}
                badgeCount={archivedProposalCount ?? 0}
                color={"info"}
                text={PageTab.Archived}
              />
            }
          />
        </Tabs>
        <FilterButtons>
          {activeTab === PageTab.Active && (
            <>
              <Chip
                icon={<FilterListIcon />}
                label="Inbox"
                color="primary"
                variant={currentBoxType === ProposalBoxType.Inbox ? "filled" : "outlined"}
                onClick={() => handleChangeBoxType(ProposalBoxType.Inbox)}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Outbox"
                color="primary"
                variant={currentBoxType === ProposalBoxType.Outbox ? "filled" : "outlined"}
                onClick={() => handleChangeBoxType(ProposalBoxType.Outbox)}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Pending Your Review"
                color="primary"
                variant={currentBoxType === ProposalBoxType.ActiveReview ? "filled" : "outlined"}
                onClick={() => handleChangeBoxType(ProposalBoxType.ActiveReview)}
              />
            </>
          )}
          {activeTab === PageTab.Drafts && (
            <Chip
              icon={<FilterListIcon />}
              label="Pending Your Review"
              color="primary"
              variant={currentBoxType === ProposalBoxType.DraftReview ? "filled" : "outlined"}
              onClick={() => handleChangeBoxType(ProposalBoxType.DraftReview)}
            />
          )}
        </FilterButtons>
      </LowerTopToolbarContainer>
      {isMediumDisplaySize && (
        <ProposalToolbarButtonsContainer>
          <ProposalToolbarButtons>
            <ToggleGlobalFilterButton table={table} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleDensePaddingButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ProposalToolbarButtons>
        </ProposalToolbarButtonsContainer>
      )}
    </ProposalTableTopToolbarContainer>
  );
}
